/**
 * <supply-buybox-form> Custom Element
 *
 * Add-to-cart for the hydration supply buybox (snippets/hydration-supply-buybox.liquid).
 *
 * Business rule:
 * - One-time purchase: add exactly the flavours picked in step 1, one of each.
 * - Subscription: the tier has a fixed total pack count — max_flavours (the
 *   "select up to N flavours" setting) * the tier's own multiplier (1/2/3
 *   month), e.g. a 3-month tier with max_flavours=2 always ships 6 packs
 *   total, NOT "3 per flavour regardless of how many flavours are picked".
 *   That total is split as evenly as possible across however many flavours
 *   are ACTUALLY selected: 2 flavours -> 3+3, 1 flavour -> all 6 of it. Any
 *   remainder from an uneven split (only possible if max_flavours isn't a
 *   clean multiple of the selected count) goes to the first-picked flavours,
 *   round-robin. See distributePacks().
 *
 * One /cart/add.js call, one line item per selected flavour, quantities from
 * the chosen tier in both modes, selling_plan set only in subscribe mode. Falls back to the single hidden `id` input
 * when there is no <supply-buybox-flavours> picker on the page (product has
 * no combined flavours) or nothing is selected.
 *
 * Every tiered add also carries a `_tier_group` property (shared by the
 * lines of one add) and a per-line `_tier_unit` (that line's packs per
 * supply), so snippets/cart-drawer.liquid can render the add as ONE supply
 * card — "28 Serves" with a stepper that moves in whole supplies — instead of
 * a "14 Pack x 2" line with a per-pack stepper. See the tier_group handling
 * there and CartSupplyGroupQuantity / CartSupplyGroupRemove in
 * assets/cart-drawer.js. Display-only: real line items stay split
 * server-side, this never touches pricing.
 *
 * The group key is deterministic (flavour variants + their unit quantities +
 * the selling plan), not random: adding the same supply twice then merges
 * into the same cart lines (Shopify merges lines whose variant, properties
 * and selling plan all match), so the card's stepper reads 2 instead of a
 * second identical card appearing.
 */
if (!customElements.get('supply-buybox-form')) {
  class SupplyBuyboxForm extends HTMLElement {
    connectedCallback() {
      this.container = this.closest('.supply-buybox') || this;
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-supply-buybox-add-to-cart]');
      this.flavoursEl = this.container.querySelector('supply-buybox-flavours');
      this.errorWrapper = this.querySelector('.supply-buybox__error-message-wrapper');
      this.errorMessage = this.querySelector('.supply-buybox__error-message');

      this.tiersStep = this.container.querySelector('[data-supply-buybox-tiers-step]');
      this.modeRadios = this.container.querySelectorAll('.supply-buybox__mode-radio');
      this.tierRadios = this.container.querySelectorAll('.supply-buybox__tier-radio');
      this.subscribeTitle = this.container.querySelector('[data-subscribe-title]');
      this.subscribeBadge = this.container.querySelector('[data-subscribe-badge]');
      this.subscribeTotalPrice = this.container.querySelector('[data-subscribe-total-price]');
      this.subscribeTotalCompare = this.container.querySelector('[data-subscribe-total-compare]');
      this.subscribePerServing = this.container.querySelector('[data-subscribe-per-serving]');
      this.subscribeHint = this.container.querySelector('[data-subscribe-hint]');
      this.onetimeTotalPrice = this.container.querySelector('[data-onetime-total-price]');
      this.onetimeSubtitle = this.container.querySelector('[data-onetime-subtitle]');
      this.atcPrice = this.querySelector('[data-atc-price]');

      this.modeRadios.forEach((radio) =>
        radio.addEventListener('change', () => {
          this.syncTiersVisibility();
          this.syncPriceDisplays();
        })
      );
      this.tierRadios.forEach((radio) => radio.addEventListener('change', () => this.syncPriceDisplays()));
      // supply-buybox-flavours.js emits this on every select/deselect —
      // both price rows are "price * flavour count", so a flavour change
      // needs the same resync as a tier change.
      this.container.addEventListener('supply-buybox:flavours-change', () => this.syncPriceDisplays());
      this.syncTiersVisibility();
      this.syncPriceDisplays();

      if (!this.form || !this.button) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    getSelectedMode() {
      const checked = this.container.querySelector('.supply-buybox__mode-radio:checked');
      return checked ? checked.value : 'onetime';
    }

    // "Choose Your Supply" stays on screen in both modes (Jono, 24 Sep): a
    // one-time purchase takes the tier's pack count too (1, 2 or 3 months of
    // pouches), just without the selling plan. What changes with the mode is
    // the per-stick figure on the tier cards: the subscribe price in
    // subscribe mode, the full price in one-time mode.
    syncTiersVisibility() {
      if (this.tiersStep) this.tiersStep.hidden = false;
      const mode = this.getSelectedMode();
      this.container.querySelectorAll('[data-tier-unit]').forEach((unit) => {
        const label = mode === 'subscribe' ? unit.dataset.subscribeLabel : unit.dataset.onetimeLabel;
        if (label) unit.textContent = label;
      });
    }

    // The panels show "price * quantity", quantity being the tier's pack
    // count (max_flavours * the tier multiplier) split across the picked
    // flavours, in both modes; one-time is charged at full price. Nobody has picked
    // flavours yet on first render, so hydration-supply-buybox.liquid
    // renders both panels assuming max_flavours; once real selections
    // exist we recompute from each flavour tile's own data-price (flavours
    // are separate products and can be priced differently) and format with
    // Shopify.formatMoney so this file never carries its own money-format
    // logic. Runs on both tier change and flavour selection change — either
    // one changes the total.
    syncPriceDisplays() {
      const selections = this.getFlavourSelections();
      const hasRealSelections = Array.isArray(selections) && selections.length > 0;

      const checkedTier = this.container.querySelector('.supply-buybox__tier-radio:checked');
      if (checkedTier) {
        const percent = parseFloat(checkedTier.dataset.savePercent || '0');
        const multiplier = parseInt(checkedTier.dataset.multiplier, 10) || 1;

        // dataset.planDisplayName ("3 Month Supply", Title Case, built in
        // Liquid the same way the tier card's own title is) — NOT
        // dataset.planName, which is Skio's raw plan name ("3 MONTH SUPPLY")
        // and also the functional key getFlavourSellingPlanId() matches
        // against per flavour. Printing that here showed ALL CAPS under
        // text-transform: none while the tier cards show Title Case.
        if (this.subscribeTitle) this.subscribeTitle.textContent = checkedTier.dataset.planDisplayName || '';
        if (this.subscribeBadge) {
          if (percent > 0) {
            this.subscribeBadge.textContent = `Save ${checkedTier.dataset.savePercent}%`;
            this.subscribeBadge.hidden = false;
          } else {
            this.subscribeBadge.hidden = true;
          }
        }
        if (this.subscribePerServing) this.subscribePerServing.textContent = checkedTier.dataset.perServingLabel || '';
        // Cadence belongs to the tier, not the panel — a single static hint
        // claimed "every 4 weeks" under the 2- and 3-month tiers too.
        if (this.subscribeHint && checkedTier.dataset.frequencyLabel) {
          this.subscribeHint.textContent = checkedTier.dataset.frequencyLabel;
        }

        const totalPacks = this.getMaxFlavours() * multiplier;
        if (hasRealSelections) {
          const quantities = this.distributePacks(totalPacks, selections.length);
          const totals = this.computeSelectionTotals(selections, quantities, percent);
          if (this.subscribeTotalPrice) this.subscribeTotalPrice.textContent = this.formatMoney(totals.totalCents);
          if (this.subscribeTotalCompare) this.subscribeTotalCompare.textContent = this.formatMoney(totals.compareCents);
          // One-time: the same packs at full price, which is the subscribe
          // compare-at figure.
          if (this.onetimeTotalPrice) this.onetimeTotalPrice.textContent = this.formatMoney(totals.compareCents);
        } else {
          if (this.subscribeTotalPrice) this.subscribeTotalPrice.textContent = checkedTier.dataset.totalPrice || '';
          if (this.subscribeTotalCompare) this.subscribeTotalCompare.textContent = checkedTier.dataset.totalCompare || '';
          if (this.onetimeTotalPrice && checkedTier.dataset.totalCompare) {
            this.onetimeTotalPrice.textContent = checkedTier.dataset.totalCompare;
          }
        }

        // Only meaningful when there is a picker to count. Without one,
        // getMaxFlavours() falls back to 1 and would contradict the server
        // render, which uses the block's max_flavours setting.
        if (this.flavoursEl) this.syncOnetimeSubtitle(totalPacks);
      } else if (hasRealSelections) {
        // No tiers (a product without selling plans): exactly what's
        // picked, 1 each.
        const quantities = selections.map(() => 1);
        const totals = this.computeSelectionTotals(selections, quantities, 0);
        if (this.onetimeTotalPrice) this.onetimeTotalPrice.textContent = this.formatMoney(totals.totalCents);
        if (this.flavoursEl) this.syncOnetimeSubtitle(selections.length);
      }

      // Add to cart button: mirror whichever total actually applies to the
      // currently-selected mode — was hardcoded to target_variant.price (a
      // single unit) forever and never reflected tier/flavour-count at all.
      if (this.atcPrice) {
        const activePriceEl = this.getSelectedMode() === 'subscribe' ? this.subscribeTotalPrice : this.onetimeTotalPrice;
        if (activePriceEl && activePriceEl.textContent) this.atcPrice.textContent = activePriceEl.textContent;
      }
    }

    // The pack and serving counts follow the tier's pack count (or, with no
    // tiers, the flavours picked). Servings-per-pack comes from the
    // same metafield hydration-supply-buybox.liquid reads, passed down as a
    // data attribute so this file never guesses 14.
    syncOnetimeSubtitle(packCount) {
      if (!this.onetimeSubtitle) return;
      const data = this.onetimeSubtitle.dataset;
      const perPouch = parseInt(data.servingsPerPouch, 10) || 0;
      const noun = packCount === 1 ? data.pouchSingular || 'Pouch' : data.pouchPlural || 'Pouches';
      const servings = perPouch * packCount;
      this.onetimeSubtitle.textContent = `${packCount} ${noun} \u2022 ${servings} Servings`;
    }

    getMaxFlavours() {
      if (this.flavoursEl && typeof this.flavoursEl.maxFlavours === 'number') return this.flavoursEl.maxFlavours;
      return 1;
    }

    // Splits totalPacks as evenly as possible across `count` flavours, extra
    // packs (when it doesn't divide evenly) going to the first-picked
    // flavours: distributePacks(6, 2) -> [3, 3], distributePacks(6, 1) ->
    // [6], distributePacks(9, 2) -> [5, 4].
    distributePacks(totalPacks, count) {
      if (count <= 0) return [];
      const base = Math.floor(totalPacks / count);
      const remainder = totalPacks % count;
      return Array.from({ length: count }, (_, i) => base + (i < remainder ? 1 : 0));
    }

    // selections: [{ tile }] from getFlavourSelections(). quantities[i] is
    // how many of selections[i] to charge for (see distributePacks above).
    // tile.dataset.price is that flavour's own full price in cents
    // (flavours are separate products and can be priced differently).
    computeSelectionTotals(selections, quantities, percent) {
      let totalCents = 0;
      let compareCents = 0;

      selections.forEach(({ tile }, index) => {
        const price = parseInt(tile && tile.dataset.price, 10) || 0;
        const quantity = quantities[index] || 0;
        compareCents += price * quantity;
        totalCents += Math.round((price * (100 - percent)) / 100) * quantity;
      });

      return { totalCents, compareCents };
    }

    formatMoney(cents) {
      if (window.Shopify && typeof Shopify.formatMoney === 'function' && window.money_format) {
        return Shopify.formatMoney(cents, window.money_format);
      }
      return String(cents);
    }

    getSelectedTier() {
      const checked = this.container.querySelector('.supply-buybox__tier-radio:checked');
      if (!checked) return null;
      return {
        sellingPlanId: checked.value,
        planName: checked.dataset.planName || '',
        multiplier: parseInt(checked.dataset.multiplier, 10) || 1,
      };
    }

    // Each flavour is its own product, so its selling plan ids are its own
    // too — the tier radio's id only belongs to the flavour it was rendered
    // from. Resolve the matching plan per flavour by name (its own
    // data-selling-plans map); only fall back to the tier's own id when a
    // flavour has no plan of that name.
    getFlavourSellingPlanId(tile, tier) {
      if (!tier.planName) return tier.sellingPlanId;

      // "name::id|name::id" — see the flavour-tile loop in
      // hydration-supply-buybox.liquid for why this isn't JSON.
      const pairs = (tile.dataset.sellingPlans || '').split('|');
      for (const pair of pairs) {
        const separatorIndex = pair.lastIndexOf('::');
        if (separatorIndex === -1) continue;
        const name = pair.slice(0, separatorIndex);
        const id = pair.slice(separatorIndex + 2);
        if (name === tier.planName) return id;
      }

      return tier.sellingPlanId;
    }

    // The duo photo for exactly these two flavours, from the hidden list in
    // sections/hydration-product-info.liquid (either order), sized for the
    // cart drawer.
    getDuoImage(selections) {
      if (!selections || selections.length !== 2) return null;
      const ids = selections.map((sel) => sel.tile && sel.tile.dataset.flavourId);
      const pair = Array.from(document.querySelectorAll('[data-hydration-flavour-art] [data-flavour-pair][data-image]')).find((el) => {
        const pairIds = el.dataset.flavourPair.split('|');
        return (pairIds[0] === ids[0] && pairIds[1] === ids[1]) || (pairIds[0] === ids[1] && pairIds[1] === ids[0]);
      });
      if (!pair) return null;
      return pair.dataset.image.replace(/([?&]width=)\d+/, '$1300');
    }

    // Stable identity of one supply: its flavour variants with their unit
    // quantities (sorted, so Mango+Watermelon and Watermelon+Mango match) and
    // the selling plan (a one-time and a subscribed supply of the same
    // flavours must stay separate cards, and Shopify would not merge those
    // lines anyway). Adding an identical supply again then merges into the
    // same lines and the card's stepper counts up.
    buildTierGroupKey(items) {
      const parts = items
        .map((item) => `${item.id}x${item.quantity}`)
        .sort();
      const plan = items[0] && items[0].selling_plan ? String(items[0].selling_plan) : 'onetime';
      return `supply:${parts.join('+')}:${plan}`;
    }

    getFlavourSelections() {
      if (!this.flavoursEl || !(this.flavoursEl.selections instanceof Map)) return null;

      return Array.from(this.flavoursEl.selections.entries()).map(([flavourId, quantity]) => {
        const tile = this.flavoursEl.tiles.find((t) => t.dataset.flavourId === flavourId);
        return { tile, variantId: tile ? tile.dataset.variantId : null, quantity };
      });
    }

    async onSubmit(event) {
      event.preventDefault();
      this.clearError();

      if (this.submitting) return;

      const mode = this.getSelectedMode();
      let multiplier = 1;
      let tier = null;

      // Both modes take the tier's quantity; only subscribe attaches the
      // selling plan (the `planTier` below).
      const hasTiers = this.tierRadios && this.tierRadios.length > 0;
      if (hasTiers) {
        tier = this.getSelectedTier();
        if (!tier) {
          this.showError('Please choose a supply option');
          return;
        }
        multiplier = tier.multiplier;
      }
      const planTier = mode === 'subscribe' ? tier : null;

      const selections = this.getFlavourSelections();
      let items;

      if (selections && selections.length) {
        items = [];
        // The tier's total pack count (max_flavours * multiplier), split
        // evenly across however many flavours are actually picked, in both
        // modes. Without tiers: exactly what's picked, 1 each.
        const quantities = tier
          ? this.distributePacks(this.getMaxFlavours() * multiplier, selections.length)
          : selections.map(() => 1);

        for (let i = 0; i < selections.length; i++) {
          const selection = selections[i];
          if (!selection.variantId) {
            this.showError('One of the selected flavours is unavailable right now');
            return;
          }
          const item = {
            id: selection.variantId,
            quantity: quantities[i],
          };
          if (planTier) item.selling_plan = this.getFlavourSellingPlanId(selection.tile, planTier);
          items.push(item);
        }

        // A tiered add is one "supply" (1/2/3 months of pouches) whatever the
        // flavour count, so every line gets the group key and its own unit —
        // a single flavour included, otherwise it lands in the cart as a
        // per-pack line that can be stepped down to one pouch. Without tiers
        // there is no supply to group. This is a display-only grouping key,
        // not tied to any Cart Transform (see the naming note in
        // snippets/cart-drawer.liquid).
        if (tier) {
          const tierGroup = this.buildTierGroupKey(items);
          // Two flavours with a duo photo set up ("Flavour pair image" block):
          // carry it on the lines so the cart drawer can show it on the merged
          // row instead of the first flavour's pack shot. Underscored, so it is
          // hidden at checkout.
          const duoImage = this.getDuoImage(selections);
          items.forEach((item) => {
            item.properties = { _tier_group: tierGroup, _tier_unit: String(item.quantity) };
            if (duoImage) item.properties._duo_image = duoImage;
          });
        }
      } else if (selections) {
        this.showError('Please select at least one flavour');
        return;
      } else {
        const fallbackId = new FormData(this.form).get('id');
        if (!fallbackId) {
          this.showError('Please select a flavour');
          return;
        }
        const item = { id: fallbackId, quantity: multiplier };
        if (planTier) item.selling_plan = planTier.sellingPlanId;
        items = [item];
        // A product with no flavour picker (one flavour, e.g. Greens Gummies)
        // still adds a whole supply, so it gets the same group key as above
        // and the cart drawer shows one supply card, not "Pouch x 3".
        if (tier) {
          item.properties = { _tier_group: this.buildTierGroupKey(items), _tier_unit: String(item.quantity) };
          // Servings per pack for the cart's "28 Serves" line. A flavour product
          // carries them in custom.measurements; a single product like Greens
          // Gummies may only have the block's servings setting, which the cart
          // drawer cannot see, so it rides along on the line.
          const packServings = this.onetimeSubtitle ? parseInt(this.onetimeSubtitle.dataset.servingsPerPouch, 10) : 0;
          if (packServings > 0) item.properties._pack_servings = String(packServings);
        }
      }

      this.submitting = true;
      const originalLabel = this.button.innerHTML;
      this.button.setAttribute('aria-disabled', 'true');
      this.button.innerHTML = 'ADDING...';

      const config = window.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({
        items,
        sections: this.getSectionsToRender().map((s) => s.section),
        sections_url: window.location.pathname,
      });

      try {
        // serializeCartAdd queues rapid adds so they don't fire concurrently.
        const doAdd = () => fetch(`${window.routes.cart_add_url}`, config);
        const response = await (window.serializeCartAdd ? window.serializeCartAdd(doAdd) : doAdd());

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(errorText);
        }

        const json = await response.json();

        if (json.status) {
          this.showError(json.description || 'Failed to add to cart');
        } else {
          try {
            this.renderLiveSections(json);
          } catch (e) {
            console.error('[SupplyBuyboxForm] Failed to render sections after add', e);
          }
          if (typeof window.runPostAddChores === 'function') window.runPostAddChores();

          this.button.innerHTML = 'ADDED';
          this.button.classList.add('altLoader');
          setTimeout(() => {
            this.button.removeAttribute('aria-disabled');
            this.button.classList.remove('altLoader');
            this.restoreButtonLabel(originalLabel);
          }, 1000);
        }
      } catch (error) {
        this.showError(error.message || 'Failed to add to cart');
        this.button.removeAttribute('aria-disabled');
        this.restoreButtonLabel(originalLabel);
      }

      this.submitting = false;
    }

    // The ADDING.../ADDED states overwrite the whole button, so putting the
    // label back also rebuilds the price span this element had cached.
    restoreButtonLabel(originalLabel) {
      this.button.innerHTML = originalLabel;
      this.atcPrice = this.querySelector('[data-atc-price]');
    }

    getSectionsToRender() {
      const sections = [];
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer) {
        sections.push({ section: 'cart-drawer' }, { section: 'cart-icon-bubble' });
      }

      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
        sections.push(...cartNotification.getSectionsToRender());
      }

      return sections;
    }

    renderLiveSections(json) {
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer) {
        cartDrawer.renderContents(json);
      } else {
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification) {
          setTimeout(() => {
            cartNotification.renderContents(json);
          }, 900);
        }
      }
    }

    showError(message) {
      if (this.errorWrapper && this.errorMessage) {
        this.errorMessage.textContent = message;
        this.errorWrapper.removeAttribute('hidden');
      }
    }

    clearError() {
      if (this.errorWrapper) this.errorWrapper.setAttribute('hidden', '');
    }
  }

  customElements.define('supply-buybox-form', SupplyBuyboxForm);
}
